import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import { StockLocationContext } from '../../../../../../context/StockLocationContext';
import { TagRelationContext } from '../../../../../../context/TagRelationContext';
import {
  CreateStockLocationResponse,
  CreateStockLocationVariables,
  StockLocationMutations,
} from '../../../../../../graphql/stockLocation.graphql';
import {
  CreateTagRelationResponse,
  CreateTagRelationVariables,
  TagRelationMutations,
} from '../../../../../../graphql/tagRelation.graphql';
import useMutation from '../../../../../../hooks/useMutation';
import { useToast } from '../../../../../../providers/ToastProvider';
import { EntityType } from '../../../../../../types/comment';
import { StockLocationInput } from '../../../../../../types/stock_location/stockLocation.input';
import { TagRelation } from '../../../../../../types/tag/tagRelation';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../types/translation/Translator';
import { Routing } from '../../../../../../util/routes.util';
import { Breadcrumbs, Crumb, StockLocationSettingsCrumb } from '../../../../../components/Breadcrumbs';
import { ButtonVariant, CreateButton } from '../../../../../components/Button';
import { TagMultiSelect } from '../../../../../components/Dropdown';
import { ToastType } from '../../../../../components/Toast';
import StockLocationInputPane from '../panes/StockLocationInputPane';

export default function StockLocationCreateScreen() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { currentCompany } = useContext(CompanyContext);
  const { stockLocations, setStockLocations } = useContext(StockLocationContext);
  const { tagRelations, setTagRelations } = useContext(TagRelationContext);

  const [input, setInput] = useState(new StockLocationInput({ companyId: currentCompany.id }));
  const [selectedTagIds, setSelectedTagIds] = useState(new Set<string>());

  const [create, { loading: createStockLocationLoading }] = useMutation<
    CreateStockLocationResponse,
    CreateStockLocationVariables
  >(StockLocationMutations.create, {
    variables: { stockLocations: [input.forCreate()] },
    onCompleted: response => {
      const stockLocation = response.createStockLocation[0];
      setStockLocations(new Map(stockLocations.set(stockLocation.id, stockLocation)));
      showToast({
        type: ToastType.success,
        title: t(TranslationKey.stockLocationCreatedSuccessfullyToast).singular.upper,
      });
    },
  });

  const [createTagRelation, { loading: createTagRelationLoading }] = useMutation<
    CreateTagRelationResponse,
    CreateTagRelationVariables
  >(TagRelationMutations.create, {
    onCompleted: response => {
      response.createTagRelation.forEach(tr => tagRelations.set(tr.id, tr));
      setTagRelations(new Map(tagRelations));
    },
  });

  const handleCreate = async () => {
    const createStockLocationResponse = await create();
    const createdLocation = createStockLocationResponse.data?.createStockLocation[0];
    if (!createdLocation) return;

    if (selectedTagIds.size) {
      await createTagRelation({
        variables: {
          tagRelations: [...selectedTagIds].map(tagId =>
            new TagRelation({ companyId: currentCompany.id })
              .withEntityId(createdLocation.id)
              .withTagId(tagId)
              .withType(EntityType.stockLocation)
              .forCreate(),
          ),
        },
      });
    }

    navigate(Routing.settings.stockLocations.overview());
  };

  const loading = createStockLocationLoading || createTagRelationLoading;

  return (
    <div className='flex h-full flex-grow flex-col'>
      <Breadcrumbs>
        <StockLocationSettingsCrumb />
        <Crumb
          text={t(TranslationKey.createNewStockLocation).singular.upper}
          path={Routing.settings.stockLocations.create()}
          current
        />
      </Breadcrumbs>

      <div className='content-x content-y-screen flex flex-grow flex-col overflow-auto'>
        <StockLocationInputPane
          input={input}
          setInput={setInput}
          tagInput={
            <TagMultiSelect
              selectedTagIds={selectedTagIds}
              entityType={EntityType.stockLocation}
              onTagSelected={tagId => {
                setSelectedTagIds(new Set(selectedTagIds).add(tagId));
              }}
              onTagCreated={tag => {
                setSelectedTagIds(new Set(selectedTagIds).add(tag.id));
              }}
              onTagRemoved={(tagId: string) => {
                selectedTagIds.delete(tagId);
                setSelectedTagIds(new Set(selectedTagIds));
              }}
            />
          }
          footer={
            <div>
              <CreateButton
                className='w-fit'
                variant={ButtonVariant.primary}
                onClick={() => handleCreate()}
                loading={loading}
                disabled={!input.validate()}
              />
            </div>
          }
        />
      </div>
    </div>
  );
}
